/**
 * PDP Media Gallery (AC-141) — vendored copy of assets/media-gallery.js (2026-07-10).
 * defines <pdp-media-gallery>.
 * Namespaced for the pdp-media-gallery block; modify THIS copy, never the original.
 * External touchpoints: see PDPGallery-Implementation-2026-07-10.md on the drive.
 */
import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./pdp-gallery-zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./pdp-gallery-slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event.
   *
   * AC-141 variant gallery (2026-07-10): when the block emitted a variant-gallery
   * JSON payload (script[data-pdp-variant-gallery], see
   * snippets/pdp-media-gallery-content.liquid), the media set is swapped IN PLACE
   * from that payload — the same <pdp-media-gallery> element stays mounted, the
   * variant-gallery slides are rebuilt, the new variant primary moves to the
   * front, thumbnails/controls/zoom-dialog re-render, and the slideshow resets
   * to slide 0 (mirroring the Figs runtime, FIGS-RUNTIME-ADDENDUM §7).
   * Without a payload (no metafield, or the block setting is off) — and on any
   * unsupported case or error — behavior is exactly the original: replace the
   * gallery with the server-rendered one from the section re-render.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const payloadScript = this.querySelector('script[type="application/json"][data-pdp-variant-gallery]');
        if (payloadScript) {
          try {
            const moduleUrl = payloadScript.getAttribute('data-src') || './pdp-gallery-variant-media.js';
            const { swapVariantMedia } = await import(moduleUrl);
            if (swapVariantMedia(this, detail)) return;
          } catch (error) {
            console.warn('[pdp-media-gallery] Variant media in-place swap failed; falling back to section swap:', error);
          }
        }

        const { html } = detail;
        const newMediaGallery = html.querySelector('pdp-media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pdp-media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('pdp-media-gallery')) {
  customElements.define('pdp-media-gallery', MediaGallery);
}
